import { BarChart3, Package } from "lucide-react"

import type { ProductResponseDto } from "@/api/api-client"
import { apiClient } from "@/api/client"
import { Separator } from "@/components/ui/separator"
import { useLanguage } from "@/hooks/useLanguage"
import {
  formatPricePerWeightUnit,
  formatTotalWeight,
  getPricePerWeightUnit,
} from "@/lib/productDisplay"
import { cn } from "@/lib/utils"

import { InventoryProductActions } from "./InventoryProductActions"
import { InventoryStockStatus } from "./InventoryStockStatus"
import type { InventoryLabels, InventoryProductCallbacks } from "./inventory-types"
import { getInventoryMargin } from "./inventory-utils"

interface InventoryProductCardProps extends InventoryProductCallbacks {
  product: ProductResponseDto
  labels: InventoryLabels
  isHighlighted: boolean
  isDeleting: boolean
}

export function InventoryProductCard({
  product,
  labels,
  isHighlighted,
  isDeleting,
  onManageBatches,
  onEdit,
  onDelete,
}: InventoryProductCardProps) {
  const imageUrl = apiClient.ensureAbsUrl(product.primaryImageUrl)
  const { t } = useLanguage()
  const perWeightUnitPrice = getPricePerWeightUnit(product)
  const hasWeight = !!product.weight && product.weight > 0

  return (
    <div
      id={`inventory-product-${product.id}`}
      className={cn(
        "group grid grid-cols-1 gap-12 rounded-lg transition-all duration-500 lg:grid-cols-12",
        isHighlighted && "ring-2 ring-accent ring-offset-4"
      )}
    >
      <div className="space-y-6 lg:col-span-3">
        <div className="relative aspect-[4/5] overflow-hidden border-[1px] border-border bg-muted shadow-xl grayscale-[0.5] transition-all duration-700 group-hover:grayscale-0">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={product.name}
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center opacity-10">
              <Package className="h-20 w-20" />
            </div>
          )}
        </div>
        <div className="space-y-2">
          <span className="text-xs font-black uppercase tracking-label text-muted-foreground">
            {labels.sku}
          </span>
          <div>
            <span className="rounded-none border-l-4 border-accent bg-foreground/90 px-3 py-1.5 font-mono text-xs font-black text-background shadow-md backdrop-blur-sm">
              {product.sku || "NO-SKU"}
            </span>
          </div>
        </div>
      </div>

      <div className="card-industrial overflow-hidden bg-muted p-px lg:col-span-6">
        <div className="h-full space-y-10 bg-card p-8">
          <div className="space-y-4">
            <div className="mb-2 flex items-center gap-3">
              <span className="border border-accent/20 bg-accent/10 px-2 py-1 text-xs font-black uppercase tracking-label text-accent">
                {product.categoryName}
              </span>
              <div className="h-px flex-1 bg-foreground/5" />
            </div>
            <h2 className="font-display text-5xl leading-none tracking-tighter transition-colors group-hover:text-accent">
              {product.name}
            </h2>
            <p className="line-clamp-3 text-sm font-medium leading-relaxed text-muted-foreground">
              {product.description}
            </p>
          </div>

          <Separator className="bg-foreground/5" />

          <div className="grid grid-cols-2 gap-12">
            <div className="space-y-4">
              <span className="inline-block border-b border-accent/20 pb-1 text-xs font-black uppercase tracking-widest text-accent">
                {labels.price}
              </span>
              <div className="space-y-1">
                <p className="font-display text-4xl tabular-nums tracking-widest">
                  {hasWeight
                    ? formatPricePerWeightUnit(perWeightUnitPrice, product.weightUnit, {
                        isTaxable: !!product.isTaxable,
                      })
                    : `$${product.price}`}
                </p>
                {hasWeight ? (
                  <p className="text-xs font-bold uppercase tracking-widest text-foreground/80">
                    {product.weight}{product.weightUnit}/{t.units.perUnit}
                    <span className="text-muted-foreground"> · ${product.price}/{t.units.perUnit}</span>
                  </p>
                ) : (
                  <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    PER {product.weightUnit}
                  </p>
                )}
                {hasWeight && (
                  <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    {product.stockQuantity} {product.stockQuantity === 1 ? t.units.unit : t.units.units}
                    {" · "}
                    {formatTotalWeight(
                      product.stockQuantity || 0,
                      product.weight,
                      product.weightUnit,
                      t.units.total
                    )}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <span className="inline-block border-b border-accent/20 pb-1 text-xs font-black uppercase tracking-widest text-accent">
                {labels.financials}
              </span>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-3 w-3 text-muted-foreground" />
                  <span className="text-xs font-bold uppercase tracking-wider text-emerald-600">
                    {getInventoryMargin(product).toFixed(1)}% {labels.margin}
                  </span>
                </div>
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  {labels.unitCost}: ${product.unitCost?.toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-between space-y-10 lg:col-span-3">
        <div className="space-y-6">
          <InventoryStockStatus product={product} labels={labels} />
        </div>

        <InventoryProductActions
          product={product}
          labels={labels}
          variant="comfortable"
          isDeleting={isDeleting}
          onManageBatches={onManageBatches}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </div>
    </div>
  )
}
